
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { clearConnectionCache } from '@/utils/etsyConnection';

const EtsyOAuthCallback = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');

      if (error) {
        console.error('OAuth error:', error);
        toast({
          title: "Connection Failed",
          description: error || "Failed to connect to Etsy",
          variant: "destructive",
        });
        navigate('/orders');
        return;
      }

      if (code && state) {
        try {
          // Get the code verifier from localStorage
          const codeVerifier = localStorage.getItem('etsy_code_verifier');
          
          if (!codeVerifier) {
            throw new Error('Code verifier not found. Please try connecting again.');
          }

          // Exchange code for token with code verifier
          const { data, error } = await supabase.functions.invoke('etsy-oauth', {
            body: { code, state, codeVerifier }
          });

          if (error) throw error;

          // Clean up the code verifier and clear connection cache
          localStorage.removeItem('etsy_code_verifier');
          clearConnectionCache(); // Force refresh of connection status

          toast({
            title: "Etsy Connected!",
            description: `Successfully connected to ${data.shopName}. Loading your data...`,
          });

          // Trigger a full page reload to ensure all hooks reinitialize and fetch data
          setTimeout(() => {
            window.location.href = '/orders';
          }, 1000);

        } catch (error: any) {
          console.error('Etsy connection error:', error);
          toast({
            title: "Connection Failed",
            description: error.message || "Failed to connect to Etsy",
            variant: "destructive",
          });
          // Clean up the code verifier even on error
          localStorage.removeItem('etsy_code_verifier');
          navigate('/orders');
        }
      } else {
        // No code or state, redirect to orders
        navigate('/orders');
      }
    };

    handleOAuthCallback();
  }, [navigate, toast]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Completing Etsy integration...</p>
      </div>
    </div>
  );
};

export default EtsyOAuthCallback;
